import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { FaPlus, FaCheck } from "react-icons/fa";
import { listProductDetails } from "../actions/productActions";
import { addToCart } from "../actions/cartActions";
import { t } from "../i18n/translations";

function Product({ product, cartItems, table }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useSelector((state) => state.language);
  const { restaurant = {} } = useSelector((state) => state.restaurantDetails || {});
  const isClosed = restaurant.is_open_now === false;
  const hidePrices = restaurant.hide_prices === true;

  // Brief checkmark flash after a quick-add, purely cosmetic — the
  // item itself is already in cartItems by the time this is true.
  const [justAdded, setJustAdded] = useState(false);

  // No image at all, or the uploaded one failed to load — either way,
  // don't reserve space for a broken/empty photo box. The row just
  // becomes text-only, same as Wolt does for photo-less items.
  const [imgFailed, setImgFailed] = useState(false);
  const hasImage = !!product.image && !imgFailed;

  // Bottle-shaped photos (wine, spirits) are far taller than wide — in
  // this fixed square thumbnail, object-fit: cover zooms in so tight only
  // a sliver of the label survives. Letterbox those instead once we know
  // the real pixel dimensions (see .product-card-image.portrait).
  const [imgPortrait, setImgPortrait] = useState(false);

  const inCart = cartItems?.some((item) => item.product === product._id);

  // Same gating ProductDetailScreen uses to show/hide its add-to-cart
  // action — restaurants below "basic" can't take online orders at all,
  // and a restaurant currently outside its posted opening hours can't
  // take orders either (server-side enforced too, see addOrderItems).
  const canOrder =
    ["basic", "pro"].includes(product?.subscription_tier?.toLowerCase()) &&
    !isClosed;

  // Anything with a required option group can't be added blind — it
  // still needs a trip through the detail screen to make that choice.
  const hasRequiredOptions = product.option_groups?.some((g) => g.required);
  const canQuickAdd = canOrder && !hasRequiredOptions;

  const openDetail = () => {
    dispatch(listProductDetails(product._id));
    navigate(
      table
        ? `/products/${product._id}?table=${table}`
        : `/products/${product._id}`,
      // Keeps the menu list mounted as the sheet's dimmed backdrop (see
      // App.js's AppRoutes) instead of navigating away from it.
      { state: { backgroundLocation: location } },
    );
  };

  // The add button is shown on every orderable item, never just some —
  // a "+" that's sometimes there and sometimes not reads as broken. What
  // it *does* on click differs instead: quick-add when nothing needs
  // choosing, otherwise it's just another way in to the option picker
  // (same as tapping the row). This mirrors how Wolt itself handles it —
  // the "+" is always present, whether or not the item needs customizing.
  const addButtonHandler = (e) => {
    e.stopPropagation();

    if (!canQuickAdd) {
      openDetail();
      return;
    }

    dispatch(
      addToCart({
        product: product._id,
        name: product.name,
        qty: 1,
        price: product.price,
        image: product.image,
        selectedOptions: [],
      }),
    );

    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 900);
  };

  const addButton = (extraClassName) => (
    <button
      type="button"
      className={`product-quick-add ${extraClassName} ${justAdded ? "added" : ""}`}
      onClick={addButtonHandler}
      aria-label={t("addToCart", language)}
    >
      {justAdded ? <FaCheck /> : <FaPlus />}
    </button>
  );

  return (
    <div
      className={`product-card ${inCart ? "in-cart" : ""}`}
      onClick={openDetail}
    >
      <div className="product-card-body">
        {inCart && (
          <div className="product-card-badge-inline">
            {t("inCartBadge", language)}
          </div>
        )}

        <div className="product-card-name">{product.name}</div>

        {product.description && (
          <div className="product-card-desc">{product.description}</div>
        )}

        <div className="product-card-bottom">
          <div className="product-card-price">
            {!hidePrices && `${product.price} Ft`}
          </div>

          {!hasImage && canOrder && addButton("product-quick-add-standalone")}
        </div>
      </div>

      {hasImage && (
        <div className="product-card-media">
          <img
            src={product.image}
            alt={product.name}
            className={`product-card-image ${imgPortrait ? "portrait" : ""}`}
            onError={() => setImgFailed(true)}
            onLoad={(e) =>
              setImgPortrait(e.target.naturalHeight > e.target.naturalWidth)
            }
          />

          {canOrder && addButton("product-quick-add-overlay")}
        </div>
      )}
    </div>
  );
}

export default Product;
